import { ProductFeatureId } from '@/actions/permissionActions'
import { UNDISPLAYED_FEATURE_IDS } from '@/constants/productFeatureConstants'
import { planChangeFeatureLabel } from './entitlementCopy'

export interface DisplayRow {
  key: string
  label: string
  holds: (featureIds: Set<string>) => boolean
}

/** The row keys `displayRowKeysFor` can produce, named so a caller never respells one. */
export const TAX_FILING_ROW_KEYS = {
  filing: 'tax_filing',
  personalIncluded: 'tax_filing_personal_included',
  businessIncluded: 'tax_filing_business_included',
  personalPaid: 'tax_filing_personal_paid',
} as const

const TAX_FEATURE_IDS: string[] = [
  ProductFeatureId.annualTaxes,
  ProductFeatureId.annualTaxesBusinessFiling,
  ProductFeatureId.free2ndTaxFiling,
]

/**
 * A plan that files a business return spends `annual_taxes` on it, so its personal return
 * needs `free_2nd_tax_filing`; a plan with no business return files the personal one under
 * `annual_taxes` itself. Deriving it here avoids granting an entitlement nobody holds.
 */
const TAX_FILING_ROWS: DisplayRow[] = [
  {
    key: TAX_FILING_ROW_KEYS.filing,
    label: 'File your taxes with Heard',
    holds: (f) => f.has(ProductFeatureId.annualTaxes),
  },
  {
    key: TAX_FILING_ROW_KEYS.personalIncluded,
    label: 'Personal tax return filed with Heard at no extra cost',
    holds: (f) =>
      f.has(ProductFeatureId.free2ndTaxFiling) ||
      (f.has(ProductFeatureId.annualTaxes) &&
        !f.has(ProductFeatureId.annualTaxesBusinessFiling)),
  },
  {
    key: TAX_FILING_ROW_KEYS.businessIncluded,
    label: 'Business tax return filed with Heard at no extra cost',
    holds: (f) => f.has(ProductFeatureId.annualTaxesBusinessFiling),
  },
  {
    key: TAX_FILING_ROW_KEYS.personalPaid,
    label: 'Personal tax return filed with Heard (additional fee)',
    holds: (f) =>
      f.has(ProductFeatureId.annualTaxesBusinessFiling) &&
      !f.has(ProductFeatureId.free2ndTaxFiling),
  },
]

const isPlainRow = (featureId: string): boolean =>
  !TAX_FEATURE_IDS.includes(featureId) &&
  !UNDISPLAYED_FEATURE_IDS.includes(featureId)

/** The rows a feature set displays as, in the order the surfaces render them. */
export const displayRowKeysFor = (featureIds: string[]): string[] => {
  const held = new Set(featureIds)
  return [
    ...featureIds.filter(isPlainRow),
    ...TAX_FILING_ROWS.filter((row) => row.holds(held)).map((row) => row.key),
  ]
}

export const displayRowLabel = (key: string): string =>
  TAX_FILING_ROWS.find((row) => row.key === key)?.label ??
  planChangeFeatureLabel(key)

/**
 * `hide` drops rows the comparison cannot state truthfully for this customer, which is
 * anything a subscription or a banked coupon serves rather than the plan.
 */
export const displayRowsFor = (
  columns: string[][],
  { hide = [] }: { hide?: string[] } = {}
): DisplayRow[] => {
  const sets = columns.map((featureIds) => new Set(featureIds))
  const plain = [...new Set(columns.flat())].filter(
    (featureId) => isPlainRow(featureId) && !hide.includes(featureId)
  )
  return [
    ...plain.map((featureId) => ({
      key: featureId,
      label: planChangeFeatureLabel(featureId),
      holds: (f: Set<string>) => f.has(featureId),
    })),
    ...TAX_FILING_ROWS.filter((row) => sets.some((set) => row.holds(set))),
  ]
}
